import React, { useState } from 'react';
import { motion } from 'framer-motion';
import ImageSlider from './ImageSlider';

// Set REACT_APP_API_URL=https://api.eazyworldehsengineering.com in frontend/.env.production
const API = process.env.REACT_APP_API_URL || 'https://apiwebsite.eazyworldehsengineering.com';

const SCOPE_OPTIONS = [
  'Lifts & Hoists (Sec 28 Rule 55)',
  'Lifting Machinery & Tackles (Sec 29 Rule 55-A)',
  'Pressure Plant Inspection (Sec 31 Rule 56)',
  'Safety Harness & Fall Protection (Sec 41 Rule 61-A)',
  'EHS Project Audit (Monthly/Quarterly)',
  'Competence Training / Workforce Evaluation',
];

const EMPTY_FORM = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  company: '',
  scope: '',
  details: '',
};

const CONTACT_INFO = [
  { icon: '📍', label: 'Address',        value: 'Old No 7, New No 17, Patel Road, Perambur, Chennai – 600 011, Tamil Nadu, India.', href: null },
  { icon: '📱', label: 'Mobile / Phone', value: '+91 90253 21004  /  044 4286 0505', href: 'tel:+919025321004' },
  { icon: '✉️', label: 'Email',          value: 'info@eazyworldehsengineering.com', href: 'mailto:info@eazyworldehsengineering.com' },
];

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] },
});

function FloatInput({ label, type = 'text', placeholder, required, value, onChange, name }) {
  const [focused, setFocused] = useState(false);
  const active = focused || (value && value.length > 0);
  return (
    <div className="relative">
      <label className={`absolute left-4 transition-all duration-200 pointer-events-none ${active ? 'top-2 text-[10px] text-brand-orange' : 'top-4 text-sm text-brand-subtle'}`}>
        {label}
      </label>
      <input
        type={type}
        name={name}
        placeholder={active ? placeholder : ''}
        required={required}
        value={value}
        onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className={`w-full bg-white border rounded-xl px-4 pt-6 pb-2.5 text-sm text-brand-heading outline-none transition-all duration-300 ${
          focused ? 'border-brand-orange shadow-orange' : 'border-gray-200 hover:border-gray-300'
        }`}
      />
    </div>
  );
}

export default function Contact() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const update = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.first_name || !form.last_name || !form.email || !form.phone) {
      setError('Please fill in your name, email, and phone.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/inspections`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Request failed');
      setSent(true);
      setForm(EMPTY_FORM);
      setTimeout(() => setSent(false), 3000);
    } catch {
      setError('Could not submit right now. Please try again or contact us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="section-pad bg-white relative overflow-hidden">
      <div className="container-xl relative z-10">
        <motion.span {...fadeUp()} className="section-label">
          <span className="w-8 h-px bg-brand-orange" /> Start A Conversation
        </motion.span>
        <motion.h2 {...fadeUp(0.1)} className="font-heading font-black text-4xl sm:text-5xl text-brand-heading mb-14 leading-tight">
          MAKE COMPLIANCE A <span className="text-gradient">PROJECT ADVANTAGE</span>
        </motion.h2>

        <div className="grid lg:grid-cols-2 gap-10">
          {/* Left – info */}
          <div>
            <motion.h3 {...fadeUp(0.15)} className="font-heading font-bold text-2xl text-brand-heading mb-8">Talk To Eazyworld</motion.h3>

            <div className="space-y-4 mb-8">
              {CONTACT_INFO.map((info, i) => (
                <motion.div
                  key={info.label}
                  {...fadeUp(0.2 + i * 0.1)}
                  className="flex items-start gap-4 card rounded-2xl p-5 hover:border-orange-200 transition-all duration-300 group"
                >
                  <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center text-lg shrink-0 group-hover:bg-orange-100 transition-colors">
                    {info.icon}
                  </div>
                  <div>
                    <div className="text-brand-orange text-xs font-bold tracking-[2px] uppercase mb-1">{info.label}</div>
                    {info.href
                      ? <a href={info.href} className="text-brand-subtle text-sm hover:text-brand-orange transition-colors">{info.value}</a>
                      : <p className="text-brand-subtle text-sm">{info.value}</p>
                    }
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div {...fadeUp(0.5)} className="surface rounded-2xl p-6 border-l-4 border-l-brand-orange">
              <p className="text-brand-orange text-xs font-bold tracking-[2px] uppercase mb-2">Requirements Needed</p>
              <p className="text-brand-subtle text-sm leading-relaxed">
                Share your asset list, site location, required inspection/audit standard, and target date. Our team will confirm scope and qualified resource availability.
              </p>
            </motion.div>
          </div>

          {/* Right – form */}
          <motion.div {...fadeUp(0.2)} className="card rounded-2xl p-8 border-t-4 border-t-brand-orange">
            <h3 className="font-heading font-bold text-xl text-brand-heading mb-6">Schedule Inspection or Audit</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <FloatInput label="First Name" name="first_name" placeholder="First name" required value={form.first_name} onChange={update('first_name')} />
                <FloatInput label="Last Name"  name="last_name"  placeholder="Last name"  required value={form.last_name}  onChange={update('last_name')} />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <FloatInput label="Email" name="email" type="email" placeholder="you@company.com" required value={form.email} onChange={update('email')} />
                <FloatInput label="Phone" name="phone" type="tel"   placeholder="+91 XXXXX XXXXX"  required value={form.phone} onChange={update('phone')} />
              </div>

              <FloatInput label="Company Name" name="company" placeholder="Your company" value={form.company} onChange={update('company')} />

              <div className="relative">
                <select
                  value={form.scope}
                  onChange={update('scope')}
                  className="w-full bg-white border border-gray-200 hover:border-gray-300 focus:border-brand-orange rounded-xl px-4 py-4 text-sm text-brand-subtle outline-none transition-all duration-300 appearance-none"
                >
                  <option value="" disabled>Select service scope...</option>
                  {SCOPE_OPTIONS.map(opt => <option key={opt}>{opt}</option>)}
                </select>
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-brand-subtle pointer-events-none">▾</span>
              </div>

              <textarea
                rows={4}
                value={form.details}
                onChange={update('details')}
                placeholder="Specify equipment count, site location, and preferred inspection timeline..."
                className="w-full bg-white border border-gray-200 hover:border-gray-300 focus:border-brand-orange rounded-xl px-4 py-4 text-sm text-brand-heading placeholder-brand-subtle outline-none transition-all duration-300 resize-none"
              />

              {error && <p className="text-xs text-red-500 -mt-1">{error}</p>}

              <motion.button
                type="submit"
                disabled={submitting}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`w-full py-4 rounded-xl font-semibold text-sm tracking-wide transition-all duration-300 disabled:opacity-60 ${
                  sent
                    ? 'bg-green-500 text-white'
                    : 'bg-brand-orange text-white hover:bg-brand-orange-l hover:shadow-orange'
                }`}
              >
                {sent ? 'Request Submitted ✓' : submitting ? 'Submitting…' : 'Submit Scope Details ↗'}
              </motion.button>
            </form>
          </motion.div>
        </div>

        <div className="mt-16">
          <ImageSlider page="contact" />
        </div>
      </div>
    </section>
  );
}
